import { apiClient } from '@/services/api';
import type { Action } from '@/types/deal';
import type { VoltrShipping, VoltrSocial, VoltrUpload, VoltrVideoFile } from '@/types/voltr';

type UploadResponse = { action: Action; upload: VoltrUpload };

/** MarketPryce Social (Voltr) deal endpoints. Every Voltr call happens server-side. */
export const voltrService = {
  /** Apply: creates the Action and joins the program (or confirms an existing membership). */
  async start(slug: string): Promise<Action> {
    const { action } = await apiClient.post<{ action: Action }>(
      `/api/v1/deals/${encodeURIComponent(slug)}/start`,
    );
    return action;
  },

  /** Membership, this brand's products and eligibility, and the Action. */
  social(slug: string): Promise<VoltrSocial> {
    return apiClient.get<VoltrSocial>(`/api/v1/deals/${encodeURIComponent(slug)}/social`);
  },

  /** Order a product. Server allocates the idempotency key; a retry replays it. */
  async requestSample(
    actionId: number,
    productId: string,
    shipping: VoltrShipping,
  ): Promise<Action> {
    const { action } = await apiClient.post<{ action: Action }>(
      `/api/v1/actions/${actionId}/sample_request`,
      { product_id: productId, shipping },
    );
    return action;
  },

  /** Start (or resume, for the same file) one video's upload. Returns the part URLs. */
  createUpload(
    actionId: number,
    file: VoltrVideoFile,
    rightsVersion: string,
    /** The sent-back video this upload stands in for. */
    replacesShareId?: string,
  ): Promise<UploadResponse> {
    return apiClient.post<UploadResponse>(`/api/v1/actions/${actionId}/uploads`, {
      file_name: file.fileName,
      mime_type: file.mimeType,
      size_bytes: file.sizeBytes,
      sha256: file.sha256,
      rights_version: rightsVersion,
      ...(replacesShareId ? { replaces_share_id: replacesShareId } : {}),
    });
  },

  /** Remove a video a coach sent back: it stops counting toward the deal. */
  async removeVideo(actionId: number, shareId: string): Promise<Action> {
    const { action } = await apiClient.post<{ action: Action }>(
      `/api/v1/actions/${actionId}/videos/${encodeURIComponent(shareId)}/remove`,
    );
    return action;
  },

  /**
   * What "Read the playbook" opens: the deal's own playbook, else the brand's
   * brief (a short-lived signed URL: fetch on tap). null when there is neither.
   */
  async briefUrl(actionId: number): Promise<string | null> {
    const { url } = await apiClient.get<{ url: string | null }>(`/api/v1/actions/${actionId}/brief`);
    return url;
  },

  /** Where one upload stands; `ready` adds the video and confirms the upload step. */
  uploadStatus(actionId: number, jobId: string): Promise<UploadResponse> {
    return apiClient.get<UploadResponse>(
      `/api/v1/actions/${actionId}/uploads/${encodeURIComponent(jobId)}`,
    );
  },
};
